import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Select, Store } from '@ngxs/store';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { SortAllUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { MatStepper } from '@angular/material/stepper';
import { ProgramItemService } from 'app/library/program-item/services/program-item.service';
import { ProgramStructureWithGroup } from 'app/library/program-item/models/program-structure-with-group.model';
import { ParticipantExecutionChange } from 'app/library/program-item/models/participant-execution-change.model';
import { TranslateService } from '@ngx-translate/core';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { Navigate } from '@ngxs/router-plugin';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleManagerPermissions } from 'app/library/module-user/models/module-manager-permissions';
import { ModuleUserPermissions } from 'app/library/module-user/enums/module-user-permissions.enum';

@Component({
    selector: 'app-add-program-managers-dialog',
    templateUrl: './add-program-managers-dialog.component.html',
    styleUrls: ['./add-program-managers-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddProgramManagersDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entitie$: Observable<User[]>;

    @Input()
    entityId: string;

    @Input()
    title: string;

    @Input()
    dataType: string;

    @Input()
    moduleId: string;

    @Input()
    list: User[] = [];

    @Input()
    moduleUser: ModuleUserExtended;

    get shouldShowNextButton(): boolean {
        return this.stepper?.selectedIndex === 0 || this.stepper?.selectedIndex === 1;
    }

    get shouldShowBookManagersButton(): boolean {
        return this.stepper?.selectedIndex === 2;
    }

    @ViewChild(MatStepper) stepper: MatStepper;

    onConfirm = new EventEmitter<User>();

    programStructuresWithGroups: ProgramStructureWithGroup[] = [];

    form: FormGroup;
    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    hideEmail: boolean;
    roleLabel: string;
    selectedExecutionIds: string[] = [];
    participantsExecutionChanges: ParticipantExecutionChange[] = [];
    isProgramStructuresWithGroupsLoading: boolean = false;
    ModuleKind = ModuleKind;
    moduleManagerPermissions = ModuleManagerPermissions;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddProgramManagersDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _programItemService: ProgramItemService,
        private _translateService: TranslateService,
        private fb: FormBuilder
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    async ngOnInit(): Promise<void> {
        // Keep dialog search local — do not write AllUsers searchTerm/sessionStorage (shared with Admin/Users).
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe(() => {
            this.loadUsersPage(true);
        });

        this.initForm();
        this.loadData();

        this.form.valueChanges.subscribe(() => {
            const controlArray = this.form.get('structures') as FormArray;
            this.selectedExecutionIds = controlArray.controls.filter((control) => control.value !== null).map((control) => control.value);
        });

        if (this.list.length) {
            this.dataSource = new AllUserDataSource(
                this.entitie$.pipe(
                    map((users) => {
                        return users?.map((item) => {
                            if (this.list.find((item2) => item2.id === item.id)) {
                                item = Object.assign(new User(), item, {
                                    selected: true,
                                });
                            }
                            return item;
                        });
                    })
                ),
                this._store,
                this._action$
            );
        } else {
            this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);
        }

        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.loadUsersPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }
        });
    }

    initForm(): void {
        this.form = this.fb.group({
            structures: this.fb.array([]),
            permissions: this.fb.array([]),
        });
    }

    get permissionsFormArray(): FormArray {
        return this.form.get('permissions') as FormArray;
    }

    getSelectedPermissions(): ModuleUserPermissions[] {
        return this.permissionsFormArray.controls
            .map((control, i) => (control.value ? this.moduleManagerPermissions.all[i].value : null))
            .filter((value): value is ModuleUserPermissions => value !== null);
    }

    async onFormControlClick(i: number, j: number, isOpened: boolean): Promise<void> {
        if (isOpened) {
            const structure = this.programStructuresWithGroups[i].structure[j];
            structure.isLoading = true;
            const options = await this._programItemService.fetchProgramExecutionCourseOfferingRelevantExecutions(this.entityId, structure.courseOffering.id);
            structure.options = options;
            structure.isLoading = false;
        }
    }

    async loadData() {
        this.moduleManagerPermissions.all.forEach(() => this.permissionsFormArray.push(new FormControl(true)));
        this.programStructuresWithGroups = await this._programItemService.fetchProgramExecutionStructureWithDefaultExecutions(this.entityId);
        this.setFormControls();
    }

    setFormControls(): void {
        const controlArray = this.form.get('structures') as FormArray;

        this.programStructuresWithGroups.forEach((group, i) => {
            group.structure.forEach((structure, j) => {
                if (structure.defaultExecution) {
                    controlArray.push(new FormControl(structure.defaultExecution.id));
                    structure.options = [structure.defaultExecution];
                } else {
                    controlArray.push(new FormControl());
                    structure.options = [];
                }
                structure.isLoading = false;
            });
        });
    }

    getFormControl(i: number, j: number): FormControl {
        const controlArray = this.form.get('structures') as FormArray;
        return controlArray.at(i * this.programStructuresWithGroups[i].structure.length + j) as FormControl;
    }

    goForward(stepper: any): void {
        if (stepper.selectedIndex < stepper.steps.length - 1) {
            stepper.next();
        }
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        const currentIndex = stepper.selectedIndex;

        if (currentIndex === 0) {
            this.title = this._translateService.instant('GENERAL.LABEl.CHOOSE_MANAGERS');
        } else if (currentIndex === 1) {
            this.title = this._translateService.instant('ADD_MANAGER.DIALOG.TITLE', { usersCount: this.list.length });
        } else if (currentIndex === 2) {
            this.title = this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_MANAGERS.LABEL.MANAGE_EXECUTIONS', { participantsCount: this.list.length });
        }
    }

    removeFromList(user: User): void {
        this.list = [...this.list.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.list = [...this.list, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    editProgramStructure(): void {
        this.dialogRef.close(false);
        this._store.dispatch(new Navigate([`/modules/${this.entityId}/edit/program-structure`]));
    }

    saveSelected(): void {
        this.dialogRef.close({ userList: this.list, moduleList: this.selectedExecutionIds });
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    async loadUsersPage(resetPageIndex = false, isUnassignedSelected = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                this.dataType,
                [],
                this.entityId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                isUnassignedSelected,
                '',
                false,
                this.moduleId
            );
        }
    }
}
